<template>
    <div class="flex flex-column relative">
        <el-table
            :data="rows"
            style="width: 100%"
            border
            height="100%"
            :class="listingClasses"
            @sort-change="sendOrder"
        >
            <el-table-column
                v-if="mainColumn"
                :key="mainColumn.name"
                fixed
                :sortable="mainColumn.sortable === true ? `custom` : false"
                :sortMethod="sortColumn"
                :prop="mainColumn.name"
                :label="mainColumn.label"
                showOverflowTooltip
                :minWidth="convertedWidth(mainColumn.width)"
            >
                <template #default="scope">
                    <div
                        class="subcell semibold"
                        v-html="
                            scope.row['formatted_' + mainColumn.name] != undefined
                                ? scope.row['formatted_' + mainColumn.name]
                                : scope.row[mainColumn.name]
                        "
                    ></div>
                </template>
            </el-table-column>
            <!-- NAME -->
            <el-table-column
                v-for="(header, index) in commonColumns"
                :key="`header-${header.name}-${header.rank}`"
                :fixed="index == 0"
                :prop="header.name"
                :label="header.label"
                :sortBy="header.name"
                showOverflowTooltip
                :sortable="header.sortable === true ? `custom` : false"
                :sortMethod="sortColumn"
                :width="convertedWidth(header.width)"
            >
                <template #header="{ column }">
                    <CompassTooltip :content="column.label" placement="top">
                        {{ column.label }}
                    </CompassTooltip>
                </template>
                <template #default="scope">
                    <template v-if="scope.row['formatted_' + header.name] != undefined">
                        <template
                            v-if="
                                typeof scope.row['formatted_' + header.name] === 'object' &&
                                scope.row['formatted_' + header.name].type == 'component'
                            "
                        >
                            <component
                                v-bind="scope.row['formatted_' + header.name].params"
                                :is="scope.row['formatted_' + header.name].name"
                                :key="`${header.name}_${scope.row.id}`"
                            ></component>
                        </template>
                        <div
                            v-else
                            class="subcell"
                            v-html="scope.row['formatted_' + header.name]"
                        />
                    </template>
                    <template v-else>{{ scope.row[header.name] }}</template>
                </template>
            </el-table-column>

            <el-table-column :label="$t.__('accounting.AgedBalanceTable.to_expire')">
                <el-table-column
                    v-for="header in amountToGoColumns"
                    :key="`header-${header.name}-${header.rank}`"
                    :prop="header.name"
                    :label="header.label"
                    :sortBy="header.name"
                    showOverflowTooltip
                    :sortable="header.sortable === true ? `custom` : false"
                    :sortMethod="sortColumn"
                    :width="convertedWidth(header.width)"
                >
                    <template #header="{ column }">
                        <CompassTooltip :content="column.label" placement="top">
                            {{ column.label }}
                        </CompassTooltip>
                    </template>
                    <template #default="scope">
                        <template v-if="scope.row['formatted_' + header.name] != undefined">
                            <template
                                v-if="
                                    typeof scope.row['formatted_' + header.name] === 'object' &&
                                    scope.row['formatted_' + header.name].type == 'component'
                                "
                            >
                                <component
                                    v-bind="scope.row['formatted_' + header.name].params"
                                    :is="scope.row['formatted_' + header.name].name"
                                    :key="`${header.name}_${scope.row.id}`"
                                ></component>
                            </template>
                            <div
                                v-else
                                class="subcell"
                                v-html="scope.row['formatted_' + header.name]"
                            />
                        </template>
                        <template v-else>{{ scope.row[header.name] }}</template>
                    </template>
                </el-table-column>
            </el-table-column>
            <el-table-column :label="$t.__('accounting.AgedBalanceTable.expired')">
                <el-table-column
                    v-for="header in amountExpiredColumns"
                    :key="`header-${header.name}-${header.rank}`"
                    :prop="header.name"
                    :label="header.label"
                    :sortBy="header.name"
                    showOverflowTooltip
                    :sortable="header.sortable === true ? `custom` : false"
                    :sortMethod="sortColumn"
                    :width="convertedWidth(header.width)"
                >
                    <template #header="{ column }">
                        <CompassTooltip :content="column.label" placement="top">
                            {{ column.label }}
                        </CompassTooltip>
                    </template>
                    <template #default="scope">
                        <template v-if="scope.row['formatted_' + header.name] != undefined">
                            <template
                                v-if="
                                    typeof scope.row['formatted_' + header.name] === 'object' &&
                                    scope.row['formatted_' + header.name].type == 'component'
                                "
                            >
                                <component
                                    v-bind="scope.row['formatted_' + header.name].params"
                                    :is="scope.row['formatted_' + header.name].name"
                                    :key="`${header.name}_${scope.row.id}`"
                                ></component>
                            </template>
                            <div
                                v-else
                                class="subcell"
                                v-html="scope.row['formatted_' + header.name]"
                            />
                        </template>
                        <template v-else>{{ scope.row[header.name] }}</template>
                    </template>
                </el-table-column>
            </el-table-column>

            <!-- ALL COLUMNS -->
        </el-table>
    </div>
</template>

<script>
import _ from 'lodash';
import ListSelector from 'vcomponents/listing/ListSelector.vue';

export default {
    props: {
        listinghash: {
            type: String,
            required: true,
        },
        rows: {
            type: Array,
            default() {
                return [];
            },
        },
        columns: {
            type: Array,
            default() {
                return [];
            },
        },
    },

    data() {
        return {
            width: {
                XS: 48,
                S: 96,
                M: 144,
                L: 176,
                XL: 196,
                XXL: 240,
            },
        };
    },

    computed: {
        amountToGoColumns() {
            return this.columns.filter((col) => /expected(.)*/gi.test(col.name));
        },
        commonColumns() {
            return this.columns.filter(
                (col) => /^(?!expected|late.*$).*/g.test(col.name) && !col.isMain,
            );
        },
        amountExpiredColumns() {
            return this.columns.filter((col) => /late(.)*/gi.test(col.name));
        },

        listingClasses() {
            let classes = '';

            if (!/^((?!chrome|android).)*safari/i.test(navigator.userAgent))
                classes += ' table-ellipsis';

            return classes;
        },

        displayedColumns() {
            return this.columns.filter((col) => !col.isMain);
        },

        mainColumn() {
            const columnIndex = _.findIndex(this.columns, { isMain: true });

            if (columnIndex != -1) {
                return this.columns[columnIndex];
            }

            return false;
        },

        display() {
            return this.$store.state[this.listinghash].display;
        },

        sorting() {
            return this.$store.state[this.listinghash].sorting;
        },

        tableDensity() {
            return this.$store.state[this.listinghash].display.density;
        },
    },

    created() {
        for (var i in this.rows) {
            this.rows[i].formatted_address =
                (this.rows[i].address_zip ? this.rows[i].address_zip : '') +
                ' ' +
                (this.rows[i].address_town ? this.rows[i].address_town : '');
        }
    },

    methods: {
        convertedWidth(value) {
            return this.width[value];
        },

        sortBy(column) {
            const newSorting = {
                field: column,
                direction: 'asc',
            };

            if (this.sorting.field == column && this.sorting.direction == 'asc') {
                newSorting.direction = 'desc';
            }

            this.$store.commit(`${this.listinghash}/updateSorting`, newSorting);
            this.$store.dispatch(`${this.listinghash}/fetch`);
        },

        sendOrder: _.debounce(function (column) {
            const newOrder = column.order == 'descending' ? 'desc' : 'asc';
            const newSorting = {
                field: column.prop,
                direction: newOrder,
            };

            this.$store.commit(`${this.listinghash}/updateSorting`, newSorting);
            this.$store.dispatch(`${this.listinghash}/fetch`);
        }, 300),

        goto(url, target) {
            window.open(url, target);
        },

        sortColumn(a, b) {
            return 0;
        },
    },

    components: {
        ListSelector,
    },
};
</script>
